import React, { useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Column, usePagination, useTable } from 'react-table';
import { Badge, Button, Card, CustomInput, Table } from 'reactstrap';
import { AiFillEdit } from 'react-icons/ai';
import { Document } from '../../types/document';
import moment from 'moment';
import { useCartContext } from '../../contexts/CartContext';
import { BsFillCartPlusFill, BsFillCartXFill } from 'react-icons/bs';
import { useMetaProvider } from '../../contexts/MetaContext';
import { formatDate } from '../../utils/date';
import { SortField } from '../documentsearch';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import AccessControl from '../../users/userprofile/accesscontrol';
import { findHiddenIndexesForDocTypes } from '../metacomponents/metautils';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'TRASH':
      return 'warning';
    case 'DELETED':
      return 'danger';
    case 'INACTIVE':
      return 'secondary';
    case 'CONVERSION_PENDING':
      return 'info';
    case 'CONVERSION_ERROR':
      return 'danger';
    case 'PENDING_CONTENT':
      return 'info';
    default:
      return 'secondary';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'TRASH':
      return 'Trash';
    case 'DELETED':
      return 'Deleted';
    case 'INACTIVE':
      return 'Hidden';
    case 'CONVERSION_PENDING':
      return 'Converting';
    case 'CONVERSION_ERROR':
      return 'Conversion Error';
    case 'PENDING_CONTENT':
      return 'Pending Content';
    default:
      return status;
  }
};

const buildDocumentDescription = (doc: Document, mappedDocumentTypes: Map<string, any>, documentIndexes: any[], documentTypes: any[]): string => {
  // Get document type
  let docType = 'document';
  if (doc.types && doc.types.length > 0) {
    const documentType = mappedDocumentTypes.get(doc.types[0]);
    if (documentType) {
      docType = documentType.description;
    }
  }

  // Get hidden indexes for this document type
  const hiddenIndexes = doc.types ? findHiddenIndexesForDocTypes(documentTypes, doc.types) : [];

  // Add first few meaningful index values with their labels (max 2)
  const indexParts: string[] = [];
  for (const documentIndex of documentIndexes) {
    if (indexParts.length >= 2) break;
    if (hiddenIndexes.includes(documentIndex.id)) continue;

    const value = doc.indexes?.[documentIndex.id];
    if (value === null || value === undefined) continue;

    let formattedValue: string;

    // Format dates
    if (documentIndex.type === 'date') {
      const date = moment(value).utc();
      formattedValue = date.format('MM/DD/YYYY');
    }
    // Format picklist values
    else if (documentIndex.type === 'picklist' && documentIndex.picklistItems) {
      const picklistItem = documentIndex.picklistItems.find((item: any) => item.key === value);
      formattedValue = picklistItem ? picklistItem.value : String(value);
    }
    // Convert to string
    else {
      formattedValue = String(value);
    }

    // Include the index description for context
    indexParts.push(`${documentIndex.description}: ${formattedValue}`);
  }

  if (indexParts.length > 0) {
    return `${docType} document, ${indexParts.join(', ')}`;
  }

  return `${docType} document`;
};

interface DocumentTableProps {
  documents: Document[];
  accountId: string;
  thumbnails: Record<string, string>;
  isCart?: boolean;
  selectDocument?: (documentid: string, selected: boolean) => void;
  selectedDocuments?: string[];
  pageSize: number;
  totalPages: number;
  updatePage?: (page: number) => void;
  controlledPage?: number;
  showInactiveTypes?: boolean;
  currentSort?: SortField;
  updateSort?: (newSort: SortField) => void;
  enableBulkSelection?: boolean;
  onSelectAll?: (selected: boolean) => void;
}

const DocumentTable = ({
  documents,
  accountId,
  thumbnails,
  isCart,
  selectDocument,
  selectedDocuments,
  pageSize,
  totalPages,
  updatePage,
  controlledPage,
  showInactiveTypes,
  currentSort,
  updateSort,
  enableBulkSelection,
  onSelectAll,
}: DocumentTableProps): JSX.Element => {
  const { metaState } = useMetaProvider();

  const mappedDocumentTypes = useMemo(() => {
    return new Map(
      metaState.allowedDocumentTypes
        .filter((type) => {
          return type.status === 'active' || (showInactiveTypes && type.status === 'inactive');
        })
        .map((type) => {
          return [type.id, type];
        }),
    );
  }, [metaState, showInactiveTypes]);

  const { cartItems, addToCart, removeFromCart } = useCartContext();

  const updateSortInternal = useCallback(
    (field: string) => {
      if (!updateSort) return;
      let ascending = true;
      if (currentSort && currentSort.field === field && currentSort.ascending) ascending = false;
      updateSort({ field: field, ascending });
    },
    [currentSort, updateSort],
  );

  const getSortIndicator = useCallback(
    (field: string) => {
      if (!currentSort) return null;
      let sortIndicator = null;
      if (currentSort.field === field) {
        sortIndicator = currentSort.ascending ? <FaArrowUp /> : <FaArrowDown />;
      }

      return sortIndicator;
    },
    [currentSort],
  );

  const allCurrentPageSelected = useMemo(() => {
    if (!selectedDocuments || documents.length === 0) return false;
    // In cart mode, consider all documents. Otherwise, only consider non-permanent documents for "select all" state
    const selectableDocs = isCart ? documents : documents.filter((doc) => !doc.permanent);
    if (selectableDocs.length === 0) return false;
    return selectableDocs.every((doc) => selectedDocuments.includes(doc.id));
  }, [documents, selectedDocuments, isCart]);

  const someSelected = useMemo(() => {
    if (!selectedDocuments || selectedDocuments.length === 0) return false;
    const selectableDocs = isCart ? documents : documents.filter((doc) => !doc.permanent);
    return selectableDocs.some((doc) => selectedDocuments.includes(doc.id));
  }, [documents, selectedDocuments, isCart]);

  const handleSelectAllChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onSelectAll) {
        onSelectAll(e.target.checked);
      }
    },
    [onSelectAll],
  );

  // Callback ref to set indeterminate state immediately when element is created/updated
  const headerCheckboxRef = useCallback(
    (element: HTMLInputElement | null) => {
      if (element) {
        element.indeterminate = someSelected && !allCurrentPageSelected;
      }
    },
    [someSelected, allCurrentPageSelected],
  );

  const showCheckboxes = isCart || enableBulkSelection;

  const columns = React.useMemo(
    () =>
      [
        showCheckboxes && {
          Header: () => {
            const isIndeterminate = someSelected && !allCurrentPageSelected;
            return enableBulkSelection && onSelectAll ? (
              <CustomInput
                type="checkbox"
                id="select-all-checkbox"
                checked={isIndeterminate || allCurrentPageSelected}
                onChange={handleSelectAllChange}
                innerRef={headerCheckboxRef}
                label=""
                aria-label="Select all documents on this page"
              />
            ) : (
              ''
            );
          },
          accessor: 'id',
          id: 'selectCheckbox',
          // eslint-disable-next-line react/display-name
          Cell: (cellProps: any) => {
            const doc = cellProps.row.original;
            const isPermanent = doc.permanent;
            // In cart mode, allow selecting permanent documents (cart operations are non-destructive)
            const shouldDisable = isPermanent && !isCart;
            const documentDescription = buildDocumentDescription(doc, mappedDocumentTypes, metaState.documentIndexes, metaState.documentTypes);
            const ariaLabel = shouldDisable ? `Select ${documentDescription} (permanent, cannot be selected)` : `Select ${documentDescription}`;

            return (
              <CustomInput
                name={`document_select_${cellProps.value}`}
                type="checkbox"
                id={`document_select_${cellProps.value}`}
                checked={selectedDocuments && selectedDocuments.includes(cellProps.value) && !shouldDisable}
                disabled={shouldDisable}
                onChange={(e) => {
                  if (selectDocument && !shouldDisable) {
                    selectDocument(cellProps.value, e.target.checked);
                  }
                }}
                label=""
                aria-label={ariaLabel}
                title={shouldDisable ? 'Permanent documents cannot be selected' : ''}
              />
            );
          },
        },
        {
          Header: '',
          accessor: 'id',
          id: 'viewerlink',
          // eslint-disable-next-line react/display-name
          Cell: (cellProps: any) => {
            const thumbnail = thumbnails[cellProps.value];
            const doc = cellProps.row.original;
            const documentDescription = buildDocumentDescription(doc, mappedDocumentTypes, metaState.documentIndexes, metaState.documentTypes);

            return (
              <>
                {thumbnail && (
                  <Link to={`/viewer/${cellProps.value}`} aria-label={`View ${documentDescription}`}>
                    <img src={`data:image/png;base64,${thumbnail}`} alt={`${documentDescription} thumbnail`}></img>
                  </Link>
                )}
              </>
            );
          },
        },
        {
          Header: () => {
            const sortFieldName = 'types';
            const sortIndicator = getSortIndicator(sortFieldName);
            return (
              <div
                style={updateSort ? { cursor: 'pointer' } : {}}
                onClick={() => {
                  updateSortInternal(sortFieldName);
                }}
              >
                Document Types {sortIndicator}
              </div>
            );
          },
          accessor: 'types',
          // eslint-disable-next-line react/display-name
          Cell: (cellProps: any) => {
            const types = cellProps.value;
            const doc = cellProps.row.original;
            const status = doc.status;
            const isPermanent = doc.permanent;

            return (
              <>
                {types.map((type: any, pos: any) => {
                  const documentType = mappedDocumentTypes.get(type);
                  if (documentType) {
                    return (
                      <>
                        <span key={`typelabel_${pos}`}>
                          <strong>{documentType.description}</strong>
                        </span>
                        <br />
                      </>
                    );
                  }
                })}
                <div style={{ marginTop: '8px' }}>
                  {status && (
                    <Badge color={getStatusColor(status)} pill style={{ marginRight: '4px' }} aria-label={`Document status: ${getStatusLabel(status)}`}>
                      {getStatusLabel(status)}
                    </Badge>
                  )}
                  {isPermanent && (
                    <Badge color="primary" pill aria-label="This is a permanent document">
                      Permanent
                    </Badge>
                  )}
                </div>
              </>
            );
          },
        },
        {
          Header: () => {
            const sortFieldName = 'indexes';
            const sortIndicator = getSortIndicator(sortFieldName);
            return (
              <div
                style={updateSort ? { cursor: 'pointer' } : {}}
                onClick={() => {
                  updateSortInternal(sortFieldName);
                }}
              >
                Indexes {sortIndicator}
              </div>
            );
          },
          accessor: 'indexes',
          // eslint-disable-next-line react/display-name
          Cell: (cellProps: any) => {
            const indexValues = cellProps.value;
            const types = cellProps.row.original.types;
            const hiddenIndexes = findHiddenIndexesForDocTypes(metaState.documentTypes, types);
            return (
              <>
                {metaState.documentIndexes.map((documentIndex, pos) => {
                  if (hiddenIndexes.includes(documentIndex.id)) return null;
                  let value = indexValues[documentIndex.id];
                  if (!value) return;
                  if (documentIndex.type === 'date') {
                    const date = moment(value).utc();
                    value = date.format('MM/DD/YYYY');
                  }
                  if (documentIndex.type === 'picklist') {
                    const picklistItems = documentIndex.picklistItems;
                    picklistItems.forEach((item) => {
                      if (item.key === value) {
                        value = item.value;
                      }
                    });
                  }
                  return (
                    <div key={`indexlabel_${pos}`} style={{ marginBottom: '5px' }}>
                      <strong>{documentIndex.description}: </strong>
                      {value}
                    </div>
                  );
                })}
                {/* {Object.keys(indexes).map((indexKey, pos) => {
                  const documentIndex = mappedDocumentIndexes[indexKey];
                  let value = indexes[indexKey];
                  if (documentIndex.type === 'date') {
                    const date = new Date(value);
                    value = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
                  }
                  if (documentIndex.type === 'picklist') {
                    const picklistItems = documentIndex.picklistItems;
                    picklistItems.forEach((item) => {
                      if (item.key === value) {
                        value = item.value;
                      }
                    });
                  }
                  if (documentIndex && value) {
                    return (
                      <div key={`indexlabel_${pos}`} style={{ marginBottom: '5px' }}>
                        <strong>{documentIndex.description}: </strong>
                        {value}
                      </div>
                    );
                  }
                })} */}
              </>
            );
          },
        },
        {
          Header: () => {
            const sortFieldName = 'created';
            const sortIndicator = getSortIndicator(sortFieldName);
            return (
              <div
                style={updateSort ? { cursor: 'pointer' } : {}}
                onClick={() => {
                  updateSortInternal(sortFieldName);
                }}
              >
                Created {sortIndicator}
              </div>
            );
          },
          accessor: 'created',
          Cell: (cellProps: any) => {
            return formatDate(cellProps.value);
          },
        },
        {
          Header: () => {
            const sortFieldName = 'modified';
            const sortIndicator = getSortIndicator(sortFieldName);
            return (
              <div
                style={updateSort ? { cursor: 'pointer' } : {}}
                onClick={() => {
                  updateSortInternal(sortFieldName);
                }}
              >
                Modified {sortIndicator}
              </div>
            );
          },
          accessor: 'modified',
          Cell: (cellProps: any) => {
            return formatDate(cellProps.value);
          },
        },
        {
          Header: '',
          accessor: 'id',
          id: 'editcell',
          // eslint-disable-next-line react/display-name
          Cell: (cellProps: any) => {
            const doc = cellProps.row.original;
            const documentDescription = buildDocumentDescription(doc, mappedDocumentTypes, metaState.documentIndexes, metaState.documentTypes);

            const actionBtns = (
              <>
                {!isCart && (
                  <AccessControl permissionId={'document'} action={'update'}>
                    <Link to={`/document/${cellProps.value}`} aria-label={`Edit ${documentDescription}`}>
                      <AiFillEdit size={25} style={{ marginBottom: '10px' }}></AiFillEdit>
                    </Link>
                  </AccessControl>
                )}
                <br />
                {!cartItems.includes(cellProps.value) ? (
                  <BsFillCartPlusFill
                    size={22}
                    color="#007bff"
                    onClick={() => {
                      addToCart(cellProps.value);
                    }}
                    style={{ cursor: 'pointer', marginTop: '5px' }}
                    aria-label={`Add ${documentDescription} to cart`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        addToCart(cellProps.value);
                      }
                    }}
                  />
                ) : (
                  <BsFillCartXFill
                    size={22}
                    color="#007bff"
                    onClick={() => removeFromCart(cellProps.value)}
                    style={{ cursor: 'pointer', marginTop: '5px' }}
                    aria-label={`Remove ${documentDescription} from cart`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        removeFromCart(cellProps.value);
                      }
                    }}
                  />
                )}
              </>
            );

            return <>{actionBtns}</>;
          },
        },
      ].filter(Boolean) as Column<Document>[],
    [
      cartItems,
      thumbnails,
      selectedDocuments,
      showCheckboxes,
      allCurrentPageSelected,
      someSelected,
      handleSelectAllChange,
      enableBulkSelection,
      onSelectAll,
      selectDocument,
      isCart,
      mappedDocumentTypes,
      metaState.documentIndexes,
      metaState.documentTypes,
    ],
  );
  const initPage = controlledPage ? controlledPage - 1 : 0;
  const data = documents;
  const {
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      manualPagination: !isCart,
      pageCount: totalPages,
      initialState: { pageSize, pageIndex: initPage },
    },
    usePagination,
  );

  useEffect(() => {
    if (!updatePage || !controlledPage || pageIndex + 1 === controlledPage) return;
    updatePage(pageIndex + 1);
  }, [pageIndex]);

  const table = document.getElementsByClassName('table-responsive')[0] as HTMLElement;
  if (table != undefined) {
    table.style.borderRadius = '.5em';
  }

  return (
    <>
      <Card>
        <Table striped hover size="sm" id="auditTable" responsive>
          <thead>
            {headerGroups.map((headerGroup) => {
              return (
                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.getHeaderGroupProps().key}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()} key={column.getHeaderProps().key}>
                      {column.render('Header')}
                    </th>
                  ))}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={row.original.id}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()} key={cell.getCellProps().key}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>
      {pageCount > 1 && (
        <div className="pagination" style={{ justifyContent: 'end', marginTop: '.5em' }}>
          <div style={{ marginRight: '.5em' }}>
            <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
              {'<<'}
            </Button>{' '}
            <Button onClick={() => previousPage()} disabled={!canPreviousPage}>
              {'<'}
            </Button>{' '}
            <Button onClick={() => nextPage()} disabled={!canNextPage}>
              {'>'}
            </Button>{' '}
            <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
              {'>>'}
            </Button>{' '}
          </div>
          <div style={{ marginRight: '.5em' }}>
            <span>
              Page{' '}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>{' '}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentTable;
